import {
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DownOutlined,
  BankOutlined,
  GlobalOutlined,
  FileTextOutlined,
  LinkOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  MailOutlined,
  ApartmentOutlined,
  EditOutlined,
  PictureOutlined,
  BgColorsOutlined,
  ExclamationCircleOutlined,
  BellOutlined,
} from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import { Button, Dropdown, Flex, Image, Progress, Space, Typography } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "../../Context/ThemeContext";
import useOrganization from "../../Hooks/useOrganization";
import { CHART_COLORS } from "../../Pages/JobPostDetails/components/StatsAndAnalytics/utils/chartColors";
import AuthService from "../../Service/auth.service";
import { useDashboardSideBar } from "../../Context/DashboardSideBarContext/DashboardSideBarContext";
import useUser from "../../Hooks/useUser";
import { calculateProfileCompletion, MissingProfileItem } from "../../Utils/profileCompletion";
import { capitalizeFirstLetter } from "../../Utils/generic-functions";
import { useProfileScreening } from "../../Context/ProfileScreeningContext";
import "./index.scss";

const Navigator = () => {
  const { isOpen, toggleSideBar } = useDashboardSideBar();
  const navigate = useNavigate();
  const { org } = useOrganization();
  const { user } = useUser();
  const [profileCompletion, setProfileCompletion] = useState<{
    percentage: number;
    missingItems: MissingProfileItem[];
  }>({ percentage: 0, missingItems: [] });

  // Get stroke color based on profile completion percentage
  const getProgressColor = (percentage: number): string => {
    if (percentage >= 100) return CHART_COLORS.success;      // Green - Complete
    if (percentage >= 70) return CHART_COLORS.info;          // Cyan - Good progress
    if (percentage >= 40) return CHART_COLORS.warning;       // Orange - Needs attention
    return CHART_COLORS.error;                                // Red - Low completion
  };

  // Icon mapping for missing items
  const getItemIcon = (key: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      orgName: <BankOutlined />,
      orgWebsite: <GlobalOutlined />,
      gst: <FileTextOutlined />,
      orgDomain: <LinkOutlined />,
      timeZone: <ClockCircleOutlined />,
      currency: <DollarOutlined />,
      supportEmail: <MailOutlined />,
      industry: <ApartmentOutlined />,
      description: <EditOutlined />,
      logo: <PictureOutlined />,
      theme: <BgColorsOutlined />,
    };
    return iconMap[key] || <ExclamationCircleOutlined />;
  };

  const isApplicationDetailsPage = useLocation().pathname.includes(
    "application-details"
  );

  const isProfileScreeningPage = useLocation().pathname === "/profile-screening";
  const shouldShowUserControls = !isProfileScreeningPage && user;

  const { isRateLimited, openRateLimitModal, screeningSessionId, submittedJobDescription } = useProfileScreening();


  useEffect(() => {
    if (org) {
      const completion = calculateProfileCompletion(org);
      setProfileCompletion(completion);
    }
  }, [org]);

  const onClickLogo = () => {
    // if (org && org?.logo && org?.orgWebsite) {
    //   window.location.replace(org?.orgWebsite);
    // } else {
    //   window.location.replace("https://www.qualifyme.ai/");
    // }
    navigate("/");
  };

  const onSignOut = async () => {
    AuthService.getInstance().signOut();
    applyCustomTheme({ themeName: "", primaryColor: "", secondaryColor: "" });
    navigate("/sign-in");
  };

  const { applyCustomTheme } = useTheme();

  const handleMenuItemClick = (item: MissingProfileItem) => {
    const params = new URLSearchParams();
    if (item.tab) {
      params.set("tab", item.tab);
    }
    const url = `${item.route}${params.toString() ? `?${params.toString()}` : ""}${item.hash || ""}`;
    navigate(url);
  };

  const renderLogoImage = () => {
    const logoSrc = org?.logo
      ? `https://hiring-assistant-files.s3.ap-south-1.amazonaws.com/organization/${org?._id}/${org?.logo}`
      : '/images/qualifyme-logo-light.png';

    return (
      <Image
        preview={false}
        src={logoSrc}
        onClick={onClickLogo}
        style={{ margin: "0px 16px", cursor: "pointer" }}
        width="48px"
        height="auto"
      />
    );
  };

  // Menu items for Progress dropdown (without Sign out)
  const progressMenuItems = profileCompletion.missingItems.map((item) => ({
    key: item.key,
    label: (
      <span style={{ color: "var(--ant-color-error, #ff4d4f)" }}>
        <span style={{ marginRight: 8 }}>{getItemIcon(item.key)}</span>
        {item.label}
      </span>
    ),
    onClick: () => handleMenuItemClick(item),
  }));

  // Menu items for Profile dropdown (Sign out only)
  const profileMenuItems = [
    {
      key: "sign-out",
      label: (
        <span>
          <LogoutOutlined style={{ marginRight: 8 }} />
          Sign out
        </span>
      ),
      onClick: onSignOut,
    },
  ];

  const userName = user
    ? `${capitalizeFirstLetter(user.firstName || "")} ${capitalizeFirstLetter(user.lastName || "")}`.trim()
    : "";

  return (
    <Flex
      justify="space-between"
      align="center"
      style={{ maxWidth: 1440, margin: "0 auto", padding: "0 16px" }}
    >
      <Flex align="center" gap="16px">
        {!isApplicationDetailsPage && (
          <Button
            type="text"
            icon={
              isOpen ? (
                <MenuUnfoldOutlined style={{ color: "#ffffff" }} />
              ) : (
                <MenuFoldOutlined style={{ color: "#ffffff" }} />
              )
            }
            onClick={toggleSideBar}
            className="mobile-menu-trigger"
            style={{ fontSize: "18px", marginRight: "8px" }}
          />
        )}
        {renderLogoImage()}
        {org && (
          <span
            className="org-name text-ellipsis text-white"
            title={org?.orgName}
            onClick={onClickLogo}
            style={{ cursor: "pointer", marginLeft: "16px", color: "#ffffff", fontWeight: 600 }}
          >
            {org?.orgName ? org?.orgName : "QualifyMe"}
          </span>
        )}
      </Flex>
      {shouldShowUserControls && (
      <Space>
        <Dropdown
          menu={{ items: progressMenuItems }}
          trigger={["hover", "click"]}
          placement="bottomRight"
        >
          <Progress
            type="circle"
            percent={profileCompletion.percentage}
            size={40}
            strokeColor={getProgressColor(profileCompletion.percentage)}
            trailColor="rgba(255, 255, 255, 0.3)"
            format={(percent) => (
              <span style={{ color: "#ffffff", fontSize: "10px", fontWeight: 500 }}>
                {percent}%
              </span>
            )}
          />
        </Dropdown>
        <Dropdown
          menu={{ items: profileMenuItems }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button
            type="text"
            style={{
              color: "#ffffff",
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "4px 12px",
            }}
          >

            <Flex vertical style={{ alignItems: "flex-start", gap: 0 }}>
              <Typography.Text
                style={{
                  color: "#ffffff",
                  fontSize: "14px",
                  fontWeight: 500,
                  lineHeight: "20px",
                }}
              >
                {userName || "User"}
              </Typography.Text>
              {/* <Typography.Text
                style={{
                  color: "rgba(255, 255, 255, 0.85)",
                  fontSize: "12px",
                  lineHeight: "16px",
                }}
              >
                {org?.orgName || "Company"}

              </Typography.Text> */}
            </Flex>
            <DownOutlined style={{ color: "#ffffff", fontSize: "12px" }} />
          </Button>
        </Dropdown>
      </Space>
      )}

      {isProfileScreeningPage && (
        <Space>
          {isRateLimited && (
            <Button
              icon={<BellOutlined />}
              onClick={openRateLimitModal}
              style={{
                color: "#fff",
                borderColor: "rgba(255,255,255,0.5)",
                background: "transparent",
              }}
            >
              Notify Me
            </Button>
          )}
          <Button
            type="primary"
            ghost
            onClick={() => {
              if (screeningSessionId) {
                // User has a screening result — pass session context to sign-up
                if (submittedJobDescription) {
                  sessionStorage.setItem("screening_job_description", submittedJobDescription);
                }
                navigate(`/sign-up?screening_session_id=${encodeURIComponent(screeningSessionId)}&cta_type=free`);
              } else {
                navigate("/sign-up");
              }
            }}
            style={{ borderColor: "rgba(255,255,255,0.8)", color: "#fff" }}
          >
            Sign Up
          </Button>
        </Space>
      )}
    </Flex>
  );
};

export default Navigator;
